import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const InductionAssignPanel = ({ inductions, workers, onAssign, onInductionChange, assigning, readOnly = false }) => {
  const { t } = useLanguage();
  const [selectedInductionId, setSelectedInductionId] = useState('');
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [error, setError] = useState('');

  // Induction selection drives the worker list: each induction carries its
  // own site_id, so derive it and let the parent fetch workers for that
  // site. Previously the dropdown only set local state, so the panel never
  // loaded workers on its own. Clearing the selection resets to []. [IX97flJV]
  const handleInductionSelect = (inductionId) => {
    setSelectedInductionId(inductionId);
    setSelectedWorkerIds([]);
    const induction = inductions?.find((ind) => ind?.id === inductionId);
    onInductionChange?.(induction?.site_id || null);
  };

  const toggleWorker = (id) => {
    setSelectedWorkerIds((prev) =>
      prev?.includes(id) ? prev?.filter((w) => w !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedWorkerIds?.length === workers?.length) {
      setSelectedWorkerIds([]);
    } else {
      setSelectedWorkerIds(workers?.map((w) => w?.id));
    }
  };

  const handleAssign = () => {
    setError('');
    if (!selectedInductionId) { setError('Please select an induction document.'); return; }
    if (!selectedWorkerIds?.length) { setError('Please select at least one worker.'); return; }
    onAssign(selectedInductionId, selectedWorkerIds);
    setSelectedWorkerIds([]);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
          <Icon name="Users" size={18} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('ramsAssignTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('inductionsAssignSubtitle')}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Induction Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('inductionsDocumentLabel')}</label>
          <select
            value={selectedInductionId}
            onChange={(e) => handleInductionSelect(e?.target?.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
          >
            <option value="">{t('inductionsSelectInduction')}</option>
            {inductions?.map((ind) => (
              <option key={ind?.id} value={ind?.id}>{ind?.title}</option>
            ))}
          </select>
        </div>

        {/* Worker Multi-Select */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">{t('ramsWorkers')}</label>
            {workers?.length > 0 && (
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs text-accent hover:underline"
              >
                {selectedWorkerIds?.length === workers?.length ? t('ramsDeselectAll') : t('ramsSelectAll')}
              </button>
            )}
          </div>

          {workers?.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-border rounded-lg">
              <Icon name="Users" size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t('inductionsSelectSiteForWorkers')}</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
              {workers?.map((worker) => (
                <label
                  key={worker?.id}
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkerIds?.includes(worker?.id)}
                    onChange={() => toggleWorker(worker?.id)}
                    className="w-4 h-4 rounded border-border text-accent focus:ring-accent/30"
                  />
                  <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-accent">
                      {worker?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-foreground">{worker?.full_name}</span>
                </label>
              ))}
            </div>
          )}

          {selectedWorkerIds?.length > 0 && (
            <p className="text-xs text-accent mt-1.5">{selectedWorkerIds?.length} {t('ramsWorkersSelected')}{selectedWorkerIds?.length !== 1 ? 's' : ''} {t('ramsSelectedLabel')}</p>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg px-3 py-2">
            <Icon name="AlertCircle" size={15} />
            <span>{error}</span>
          </div>
        )}

        <Button
          onClick={handleAssign}
          disabled={assigning || !selectedInductionId || !selectedWorkerIds?.length || readOnly}
          className="w-full"
        >
          {assigning ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />{t('ramsAssigning')}</>
          ) : (
            <><Icon name="UserPlus" size={16} className="mr-2" />{t('inductionsAssignButton')}</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default InductionAssignPanel;
